import React from 'react';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { useLocalErrorForPreviewKey } from 'js/file_viewer/core/atoms/error';
import { allArchiveSubpathsAtom } from 'js/file_viewer/core/atoms/metadata';
import { rootFileInfoAtom, sharedLinkInfoAtom } from 'js/file_viewer/core/atoms/metadata';
import { useFileViewerAtomValue, usePreviewKeyAtomValue } from 'js/file_viewer/core/atoms/use_atom';
import { prepareArchiveQueryFileInfo, validatePreviewMetadata, } from 'js/file_viewer/core/utils/support';
import { makeArchiveFilesRequestPackage } from 'js/react_query_helpers/queries/archive_files';
export const prefetchArchiveFiles = (queryClient, rootFileInfo, subpaths, isLoggedIn, apiV2ClientShim, subpathSizes) => {
    // Remove any extra fields on rootFileInfo because the endpoint doesn't like them.
    const fileInfo = {
        file_id: rootFileInfo.file_id,
        ns_id: rootFileInfo.ns_id,
        sj_id: rootFileInfo.sj_id,
        url: rootFileInfo.url,
    };
    const args = {
        root_file_info: fileInfo,
        subpaths,
        subpath_sizes: subpathSizes,
    };
    const requestPackage = makeArchiveFilesRequestPackage(apiV2ClientShim.previewsGetArchiveFiles, apiV2ClientShim.previewsLoggedOutGetArchiveFiles);
    if (subpaths.length > 0) {
        requestPackage.prefetchQuery(queryClient, {
            apiArg: args,
            pkgArg: { isLoggedIn },
        });
    }
};
const useArchiveFilesQuery = (rootFileInfo, subpaths, isLoggedIn, nestedArchivePreviewKey, subpathSizes) => {
    const context = useFileViewerContext();
    if (!context) {
        throw new Error('useArchiveFilesQuery must be called within a FileViewerContext');
    }
    const { apiV2ClientShim } = context;
    const args = {
        root_file_info: rootFileInfo,
        subpaths,
        subpath_sizes: subpathSizes,
    };
    const requestPackage = React.useMemo(() => makeArchiveFilesRequestPackage(apiV2ClientShim.previewsGetArchiveFiles, apiV2ClientShim.previewsLoggedOutGetArchiveFiles), [apiV2ClientShim]);
    return requestPackage.useQuery({ apiArg: args, pkgArg: { isLoggedIn } }, { enabled: !!nestedArchivePreviewKey && subpaths.length > 0 });
};
export const useNestedArchivePreviewMetadata = (nestedPreviewKey) => {
    const { isLoggedIn } = useFileViewerContext();
    const rootFileInfo = useFileViewerAtomValue(rootFileInfoAtom);
    const sharedLinkInfo = useFileViewerAtomValue(sharedLinkInfoAtom);
    const file = prepareArchiveQueryFileInfo(rootFileInfo, sharedLinkInfo);
    const allSubpaths = usePreviewKeyAtomValue(allArchiveSubpathsAtom, nestedPreviewKey);
    const localError = useLocalErrorForPreviewKey(nestedPreviewKey);
    const { data, error } = useArchiveFilesQuery(file, allSubpaths || [], isLoggedIn, nestedPreviewKey);
    const archiveFiles = data === null || data === void 0 ? void 0 : data.apiData.archive_files;
    const validatedPreviewData = React.useMemo(() => {
        if (!(nestedPreviewKey === null || nestedPreviewKey === void 0 ? void 0 : nestedPreviewKey.subpath) || !archiveFiles) {
            return undefined;
        }
        const archiveFile = archiveFiles.find((file) => file.subpath === (nestedPreviewKey === null || nestedPreviewKey === void 0 ? void 0 : nestedPreviewKey.subpath));
        if (!archiveFile) {
            return undefined;
        }
        return validatePreviewMetadata(archiveFile.preview, error, localError, archiveFile.filename, 
        // get_archive_data doesn't provide file size.
        0, undefined);
    }, [archiveFiles, error, localError, nestedPreviewKey === null || nestedPreviewKey === void 0 ? void 0 : nestedPreviewKey.subpath]);
    return validatedPreviewData;
};
//# sourceMappingURL=archive_files.preenvesnextjs.map
